<template>
  <div>
    <b-loading :is-full-page="true" :active="!initialized"></b-loading>
    <section v-if="initialized" class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">
            {{ added ? 'Create New Episode' : 'Edit Episode' }}
          </h1>
          <p v-if="!added && item.showTitle" class="subtitle is-5 has-text-grey">
            {{ item.showTitle }}
            <span v-if="item.seasonNumber"> - Season {{ item.seasonNumber }}</span>
            <span v-if="item.episodeNumber"> - Episode {{ item.episodeNumber }}</span>
          </p>
        </div>
        <div class="columns is-vcentered">
          <div class="column is-6 is-offset-3">
            <div class="flex-card light-bordered light-raised">
              <div class="card-body">
                <ValidationObserver ref="observer">
                  <form @submit.prevent="onSubmitClick">
                    <div class="columns is-multiline">
                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Title"
                            rules="required"
                          >
                            <b-field
                              label="Title"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.title" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-12">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Description"
                          >
                            <b-field
                              label="Synopsis / Description"
                              :type="{ 'is-danger': errors.length > 0 || (item.description && item.description.length > 180) }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.description"
                                type="textarea"
                                maxlength="500"
                              />
                            </b-field>
                            <p :class="['help', { 'is-danger': item.description && item.description.length > 180 }]">
                              {{ item.description ? item.description.length : 0 }} / 180 characters
                              <span v-if="item.description && item.description.length > 180" class="has-text-weight-bold">
                                (EPG limit exceeded - please shorten)
                              </span>
                            </p>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Episode No"
                            rules="required"
                          >
                            <b-field
                              label="Episode No"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input
                                v-model="item.episodeNumber"
                                type="number"
                              />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Length in Minutes"
                          >
                            <b-field label="Length in Minutes">
                              <div class="field has-addons">
                                <div class="control is-expanded">
                                  <b-input
                                    v-model="item.episodeLength"
                                    type="number"
                                    rules="required"
                                    :disabled="segmentCount > 0"
                                  />
                                </div>
                                <div class="control" v-if="!added && segmentCount > 0">
                                  <b-tooltip label="Recalculate from segments" position="is-left">
                                    <b-button
                                      type="is-info"
                                      icon-left="refresh"
                                      @click="recalculateEpisodeLengthFromSegments"
                                    />
                                  </b-tooltip>
                                </div>
                              </div>
                              <p v-if="segmentCount > 0" class="help has-text-grey">
                                Duration locked — edit segment durations below, then click
                                <b-icon icon="refresh" size="is-small" /> to recalculate.
                              </p>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Release Date"
                          >
                            <b-field label="Release Date">
                              <b-datepicker
                                v-model="item.releaseDate"
                                placeholder="Click to select..."
                                icon="calendar-today"
                              >
                              </b-datepicker>
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>
                      <div class="column is-6-desktop">
                        <div class="control">
                          <ValidationProvider
                            v-slot="{ errors }"
                            name="Age Restriction"
                          >
                            <b-field
                              label="Age Restriction"
                              :type="{ 'is-danger': errors.length > 0 }"
                              :message="errors[0]"
                            >
                              <b-input v-model="item.ageRestriction" />
                            </b-field>
                          </ValidationProvider>
                        </div>
                      </div>

                      <!-- EPG Country Blockouts -->
                      <div class="column is-12">
                        <b-field label="Country Blockouts (Override Season/Channel)">
                          <b-taginput
                            v-model="item.epgBlockouts"
                            :data="filteredBlockoutCountries"
                            autocomplete
                            :allow-new="false"
                            :open-on-focus="true"
                            field="name"
                            icon="earth"
                            placeholder="Leave empty to inherit from season/channel..."
                            @typing="filterBlockoutCountries"
                          >
                            <template #default="props">
                              <strong>{{ props.option.name }}</strong>
                              <span v-if="props.option.note" class="has-text-grey"> - {{ props.option.note }}</span>
                            </template>
                            <template #empty>
                              No countries found
                            </template>
                          </b-taginput>
                        </b-field>
                        <p class="help">
                          Only set if this episode needs different blockouts than the season. Leave empty to inherit from season/channel.
                        </p>
                      </div>

                      <div v-if="!added" class="column is-12">
                        <div class="control">
                          <UploadMediaAssets
                            :episode-id="item.id"
                            :media-id="item.mediaId"
                            :tx-filename="item.txFilename"
                            :storage-path="item.storagePath"
                            :stitched-file-path="item.stitchedFilePath"
                            :stitch-status="item.stitchStatus"
                            :stitch-qc-status="item.stitchQcStatus"
                            @upload-complete="onUploadComplete"
                          ></UploadMediaAssets>
                        </div>
                      </div>

                      <!-- File Information Section (Simplified - source linking is now in Segment Stitching section) -->
                      <template v-if="!added">
                        <div class="column is-12">
                          <hr
                            style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                          />
                          <h3
                            class="subtitle is-5"
                            style="margin-bottom: 15px;"
                          >
                            <span class="icon">
                              <i class="mdi mdi-file-video"></i>
                            </span>
                            File & Delivery Information
                          </h3>
                        </div>

                        <div class="column is-6-desktop">
                          <b-field
                            label="TX Filename (Playout)"
                            label-position="on-border"
                          >
                            <b-input
                              v-model="item.txFilename"
                              readonly
                              icon="television"
                              placeholder="Generated after processing"
                              :expanded="true"
                            />
                            <p class="control">
                              <span class="button is-static" style="min-width: 70px;">
                                <strong>.{{ item.txFileExtension || 'mp4' }}</strong>
                              </span>
                            </p>
                            <p class="control">
                              <b-dropdown aria-role="list" position="is-bottom-left">
                                <template #trigger>
                                  <b-button
                                    icon-left="pencil"
                                    size="is-small"
                                    type="is-light"
                                    title="Fix extension if incorrect"
                                  />
                                </template>
                                <b-dropdown-item
                                  v-for="ext in ['mp4', 'mxf', 'mov']"
                                  :key="ext"
                                  aria-role="listitem"
                                  @click="updateTxFileExtension(ext)"
                                  :class="{ 'is-active': item.txFileExtension === ext }"
                                >
                                  .{{ ext }}
                                  <b-icon
                                    v-if="item.txFileExtension === ext"
                                    icon="check"
                                    size="is-small"
                                    class="ml-2"
                                  />
                                </b-dropdown-item>
                              </b-dropdown>
                            </p>
                          </b-field>
                          <p class="help">
                            System-generated filename for playout systems. Extension used in playlist exports.
                          </p>
                        </div>

                        <div class="column is-6-desktop">
                          <b-field
                            label="VOD/Catchup Filename"
                            label-position="on-border"
                          >
                            <b-input
                              v-model="item.vodFilename"
                              icon="cloud-download"
                              placeholder="Custom filename for VOD delivery"
                              :disabled="!item.vodDeliveryEnabled"
                            />
                          </b-field>
                          <div class="vod-delivery-toggle">
                            <b-checkbox
                              v-model="item.vodDeliveryEnabled"
                              type="is-success"
                            >
                              <span class="has-text-weight-medium">Enable VOD Delivery</span>
                            </b-checkbox>
                            <b-tooltip
                              label="When enabled, this episode will be encoded and delivered to the configured VOD destination after TX processing"
                              position="is-right"
                              multilined
                            >
                              <b-icon icon="information-outline" size="is-small" />
                            </b-tooltip>
                          </div>
                          <p class="help">
                            Enable to deliver to broadcaster VOD/Catchup platform
                          </p>
                        </div>

                        <!-- Workflow Status Section (consolidated) -->
                        <div class="column is-12">
                          <hr style="margin: 20px 0; border-top: 2px solid #dbdbdb;" />
                          <h3 class="subtitle is-5" style="margin-bottom: 15px;">
                            <span class="icon">
                              <i class="mdi mdi-check-decagram"></i>
                            </span>
                            Workflow Status
                          </h3>
                        </div>

                        <!-- Consolidated Status Bar -->
                        <div class="column is-12">
                          <div class="workflow-status-bar">
                            <div class="status-item">
                              <span class="status-label">TX File</span>
                              <b-tag
                                :type="getTxFileStatusType()"
                                :class="{ 'is-clickable': item.stitchQcResultId }"
                                @click.native="item.stitchQcResultId ? showQcResult() : null"
                              >
                                {{ getTxFileStatusLabel() }}
                              </b-tag>
                              <b-button
                                v-if="item.stitchQcResultId"
                                type="is-info"
                                size="is-small"
                                icon-left="file-document"
                                class="ml-1"
                                @click="showQcResult"
                              >
                                QC
                              </b-button>
                            </div>
                            <div class="status-item">
                              <span class="status-label">Link</span>
                              <b-tag :type="getLinkStatusType()">
                                {{ getLinkStatusLabel() }}
                              </b-tag>
                            </div>
                            <div class="status-item" v-if="item.linkStatus === 'confirmed'">
                              <span class="status-label">Staging</span>
                              <b-tag :type="getStagingStatusType()">
                                {{ getStagingStatusLabel() }}
                              </b-tag>
                            </div>
                            <div class="status-item">
                              <span class="status-label">Playout</span>
                              <b-tag :type="getPlayoutDeliveryStatusType()">
                                {{ getPlayoutDeliveryStatusLabel() }}
                              </b-tag>
                            </div>
                          </div>
                        </div>

                        <!-- Approve for Playout (only when ready) -->
                        <div v-if="item.txFilename" class="column is-12">
                          <div class="playout-approval-row">
                            <b-checkbox
                              v-model="item.playoutDeliveryEnabled"
                              type="is-info"
                              :disabled="!canApprovePlayoutDelivery"
                            >
                              <span class="has-text-weight-medium">Approve for Playout</span>
                            </b-checkbox>
                            <b-tooltip
                              :label="getPlayoutApprovalTooltip()"
                              position="is-right"
                              multilined
                            >
                              <b-icon icon="information-outline" size="is-small" class="ml-1" />
                            </b-tooltip>
                            <b-tag v-if="!canApprovePlayoutDelivery" type="is-warning" size="is-small" class="ml-2">
                              QC must pass first
                            </b-tag>
                          </div>
                        </div>

                        <!-- Warnings (Version Change, Soft-Segmentation) -->
                        <div v-if="item.txWorkflowStatus === 'version_changed'" class="column is-12">
                          <b-message type="is-danger" size="is-small" has-icon>
                            <strong>TX File Changed!</strong> The S3 file has been modified since approval.
                            Re-verify before export.
                          </b-message>
                        </div>
                        <div v-if="item.needsSoftSegmentation && (item.storagePath || item.stitchedFilePath)" class="column is-12">
                          <b-message type="is-warning" size="is-small" has-icon>
                            <strong>Soft-segmentation required</strong> — Adjust segment timecodes before playout.
                          </b-message>
                        </div>

                        <!-- Confirmation Info (when confirmed) -->
                        <div v-if="item.linkStatus === 'confirmed' && item.confirmedAt" class="column is-12">
                          <p class="has-text-success is-size-7">
                            <span class="icon is-small"><i class="mdi mdi-check-circle"></i></span>
                            Confirmed {{ formatDate(item.confirmedAt) }}
                            <span v-if="item.confirmedBy"> by {{ item.confirmedBy }}</span>
                          </p>
                        </div>

                        <!-- TX File Link Actions (only show when soft_linked) -->
                        <div v-if="item.linkStatus === 'soft_linked'" class="column is-12">
                          <div class="link-actions-box">
                            <div class="level">
                              <div class="level-left">
                                <div class="level-item">
                                  <span class="icon has-text-warning"><i class="mdi mdi-file-link mdi-24px"></i></span>
                                </div>
                                <div class="level-item">
                                  <div>
                                    <p class="has-text-weight-medium">TX File Ready for Confirmation</p>
                                    <p class="has-text-grey is-size-7">Review and confirm to proceed with playout delivery.</p>
                                  </div>
                                </div>
                              </div>
                              <div class="level-right">
                                <div class="level-item">
                                  <b-button
                                    type="is-success"
                                    icon-left="check"
                                    @click="confirmTxLink"
                                    :loading="confirmingLink"
                                  >
                                    Confirm
                                  </b-button>
                                </div>
                                <div class="level-item">
                                  <b-button
                                    type="is-danger"
                                    icon-left="close"
                                    outlined
                                    @click="rejectTxLink"
                                    :loading="rejectingLink"
                                  >
                                    Reject
                                  </b-button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Media Player Preview with Audio Monitor -->
                        <!-- Priority: txFilePath (linked) > stitchedFilePath (output) > storagePath (source) -->
                        <div v-if="effectivePlaybackPath" class="column is-12">
                          <hr
                            style="margin: 20px 0; border-top: 2px solid #dbdbdb;"
                          />
                          <h3
                            class="subtitle is-5"
                            style="margin-bottom: 15px;"
                          >
                            <span class="icon">
                              <i class="mdi mdi-play-circle"></i>
                            </span>
                            Episode Preview
                            <span class="is-size-7 has-text-grey ml-2">(Press F or double-click for fullscreen)</span>
                            <b-tag v-if="playbackSource" type="is-info" size="is-small" class="ml-2">{{ playbackSource }}</b-tag>
                          </h3>
                          <div class="episode-preview-wrapper">
                            <div class="video-container">
                              <MediaPlayer
                                ref="mediaPlayer"
                                :storage-path="effectivePlaybackPath"
                                :storage-config-id="item.storageConfigId"
                                :source-filename="effectivePlaybackFilename"
                                :media-title="
                                  `${item.title} - Episode ${item.episodeNumber}`
                                "
                                :autoplay="false"
                                :show-info="true"
                                @play="onVideoPlay"
                              />
                            </div>
                            <!-- Audio Monitor disabled - requires CORS config on S3 buckets
                            <div class="audio-monitor-container">
                              <AudioMonitor
                                :video-element="audioMonitorVideoElement"
                                :compact="true"
                              />
                            </div>
                            -->
                          </div>
                        </div>
                      </template>
                    </div>
                    <div class="has-text-centered">
                      <div class="is-block has-margin-bottom-15">
                        <b-button
                          icon-left="arrow-left"
                          class="is-medium has-margin-right-10"
                          @click="backToManageEpisodes"
                        >
                          Back to List</b-button
                        >
                        <button
                          class="button is-primary is-medium btn-align no-lh raised primary-btn"
                          type="submit"
                          :disabled="loading"
                          :class="{ 'is-loading': loading }"
                        >
                          {{ added ? 'Create' : 'Update' }}
                        </button>
                        <b-button
                          v-if="!added"
                          type="is-warning"
                          size="is-medium"
                          icon-left="history"
                          @click="showRecentChanges"
                        >
                          Recent Changes
                        </b-button>
                      </div>
                    </div>
                  </form>
                </ValidationObserver>
              </div>
            </div>
          </div>
        </div>
      </CardComponent>

      <!-- Segments Manager Section -->
      <CardComponent
        v-if="!added && item.id"
        title="Episode Segments"
        class="has-margin-top-20"
      >
        <SegmentsManager :episode-id="item.id" />
      </CardComponent>

      <!-- Episode Processing Panel (Single/Multi mode with workflow selection) -->
      <EpisodeProcessingPanel
        v-if="!added && item.id"
        class="has-margin-top-20"
        :episode-id="item.id"
        :show-title="item.showTitle"
        :season-number="item.seasonNumber"
        :episode-number="item.episodeNumber"
        :media-id="item.mediaId"
        :start-open="false"
        @job-submitted="onStitchJobSubmitted"
        @files-linked="onFilesLinked"
        @qc-status-changed="onQcStatusChanged"
        @approve-playout="onApprovePlayoutFromPanel"
      />
    </section>
    <RecentChangesModal
      :show="showRecentChangesModal"
      @refresh="refreshData"
      @close-modal="showRecentChangesModal = false"
    ></RecentChangesModal>

    <!-- QC Result Modal -->
    <ViewQcResultModal
      v-if="showViewQcResultModal"
      :show="showViewQcResultModal"
      :qc-result-id="item.stitchQcResultId"
      @close-modal="showViewQcResultModal = false"
      @refresh="refreshData"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CardComponent from '@/components/CardComponent'
import MediaApi from '@/api/media'
import EpisodeApi from '@/api/episodes'
import SegmentsApi from '@/api/segments'
import TrafficAgentApi from '@/api/trafficAgent'
import StorageBrowseApi from '@/api/storageBrowse'
import BaseDateTimePicker from '@/components/BaseDateTimePicker'
import UploadMediaAssets from '@/components/UploadMediaAssets'
import SegmentsManager from '@/components/SegmentsManager'
import EpisodeProcessingPanel from '@/components/EpisodeProcessingPanel'
import MediaPlayer from '@/components/MediaPlayer'
import AudioMonitor from '@/components/AudioMonitor'
import RecentChangesModal from '@/components/RecentChangesModal'
import ViewQcResultModal from '@/components/ViewQcResultModal'
import episodeEventBus, { EPISODE_EVENTS } from '@/services/episodeEventBus'

export default {
  name: 'EditMedia',
  components: {
    CardComponent,
    BaseDateTimePicker,
    UploadMediaAssets,
    SegmentsManager,
    EpisodeProcessingPanel,
    MediaPlayer,
    AudioMonitor,
    RecentChangesModal,
    ViewQcResultModal
  },
  metaInfo: {
    title: 'Edit Episode'
  },
  computed: {
    ...mapGetters(['user']),

    /**
     * Can user approve for playout delivery?
     * Requires: TX filename exists AND QC passed/overridden
     */
    canApprovePlayoutDelivery() {
      if (!this.item.txFilename) return false

      const passedStatuses = ['passed', 'passed_with_warnings', 'overridden']
      return passedStatuses.includes(this.item.stitchQcStatus)
    },

    /**
     * Effective playback path - prioritizes linked/processed files over source
     * Priority: txFilePath (linked TX) > stitchedFilePath (stitch output) > storagePath (source)
     */
    effectivePlaybackPath() {
      // TX file path from Traffic Agent linking has highest priority
      if (this.item.txFilePath) return this.item.txFilePath
      // Stitched/transcode output has second priority
      if (this.item.stitchedFilePath) return this.item.stitchedFilePath
      // Original source file as fallback
      return this.item.storagePath || ''
    },

    /**
     * Label indicating which file source is being played
     */
    playbackSource() {
      if (this.item.txFilePath) return 'Linked TX File'
      if (this.item.stitchedFilePath) return 'Processed Output'
      if (this.item.storagePath) return 'Source File'
      return ''
    },

    /**
     * Filename for the effective playback path
     */
    effectivePlaybackFilename() {
      const path = this.effectivePlaybackPath
      if (!path) return ''
      // Extract filename from path
      const parts = path.split('/')
      return parts[parts.length - 1] || ''
    },

    /**
     * Snapshot of the operator-editable fields. Used by isDirty.
     * Only includes fields that the form binds to — not status/workflow fields
     * which legitimately change server-side without operator input.
     */
    editableFormFields() {
      return {
        title: this.item.title,
        description: this.item.description,
        episodeNumber: this.item.episodeNumber,
        ageRestriction: this.item.ageRestriction,
        episodeLength: this.item.episodeLength,
        releaseDate: this.item.releaseDate,
        vodStartDate: this.item.vodStartDate,
        vodExpiryDate: this.item.vodExpiryDate,
        vodFilename: this.item.vodFilename,
        vodDeliveryEnabled: this.item.vodDeliveryEnabled,
        playoutDeliveryEnabled: this.item.playoutDeliveryEnabled,
        epgBlockouts: (this.item.epgBlockouts || []).map(c => c.name || c).sort()
      }
    },

    /**
     * True when the operator has made unsaved changes to any editable field.
     * Drives beforeRouteLeave + beforeunload guards.
     */
    isDirty() {
      if (!this.initialSnapshot) return false
      return JSON.stringify(this.editableFormFields) !== this.initialSnapshot
    }
  },
  watch: {
    async $route() {
      if (!this.added) {
        await this.setData()
        this.captureFormSnapshot() // Re-capture pristine state for new episode
      }
    }
  },
  async mounted() {
    this.added = this.$route.name === 'CreateEpisode' ? true : false
    if (!this.added) {
      await this.setData()
    }
    // Initialize EPG blockout countries filter
    this.filteredBlockoutCountries = this.blockoutCountries
    this.initialized = true

    // Capture pristine form state for dirty detection (Fix 3 — unsaved changes guard)
    this.captureFormSnapshot()

    // Browser-level guard for tab close / refresh / external nav
    window.addEventListener('beforeunload', this.onBeforeUnload)

    // Listen for episode status changes from ManageEpisodes
    episodeEventBus.$on(EPISODE_EVENTS.STATUS_CHANGED, this.handleExternalStatusChange)
    episodeEventBus.$on(EPISODE_EVENTS.REFRESH_REQUESTED, this.handleExternalRefreshRequest)
  },
  beforeRouteLeave(to, from, next) {
    // In-app navigation guard. Browser-level closes go through onBeforeUnload.
    if (this.isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes to this episode. They will be lost if you leave now.\n\nLeave anyway?'
      )
      next(confirmed)
    } else {
      next()
    }
  },
  beforeDestroy() {
    // Clean up event listeners
    episodeEventBus.$off(EPISODE_EVENTS.STATUS_CHANGED, this.handleExternalStatusChange)
    episodeEventBus.$off(EPISODE_EVENTS.REFRESH_REQUESTED, this.handleExternalRefreshRequest)
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  data() {
    return {
      loading: false,
      initialized: false,
      added: false,
      showRecentChangesModal: false,
      showViewQcResultModal: false,
      segmentCount: 0,
      segmentTotalMinutes: 0,
      linkedSegmentCount: 0, // Track how many segments have source files linked
      // Traffic Agent UI state
      confirmingLink: false,
      rejectingLink: false,
      loadingTxPreview: false,
      txPreviewUrl: null,
      audioMonitorVideoElement: null,
      // Dirty-form tracking (Fix 3 — unsaved changes guard)
      initialSnapshot: null,
      item: {
        id: null,
        title: '',
        description: '',
        episodeNumber: null,
        mediaId: null,
        showTitle: '',
        seasonNumber: null,
        sourceFilename: '',
        storagePath: '',
        stitchedFilePath: '',
        txFilename: '',
        txFileExtension: '',
        vodFilename: '',
        vodDeliveryEnabled: false,
        playoutDeliveryEnabled: false,
        playoutDeliveryStatus: 'not_started',
        playoutApprovedAt: null,
        playoutDeliveredAt: null,
        playoutDestinationPath: '',
        playoutDeliveryError: '',
        fileStatus: 'pending',
        fileOrigin: 'new',
        stitchStatus: 'not_started',
        stitchQcStatus: null,
        stitchQcResultId: null,
        // TX Workflow Status and Version Tracking
        txWorkflowStatus: 'not_linked',
        txFileEtag: null,
        txFileEtagAt: null,
        txFileSizeBytes: null,
        needsSoftSegmentation: false,  // Default to false - assume properly segmented unless explicitly marked
        // Traffic Agent link status fields
        linkStatus: 'not_linked',
        linkSource: null,
        confirmedBy: null,
        confirmedAt: null,
        txFilePath: '',
        smbStagingStatus: 'not_staged',
        smbStagedAt: null,
        smbStagedPath: '',
        smbStagingError: '',
        releaseDate: null,
        ageRestriction: '',
        episodeLength: null,
        vodStartDate: null,
        vodExpiryDate: null,
        episodedata: {},
        // EPG Configuration
        epgBlockouts: []
      },
      // EPG Country Blockouts - All Sub-Saharan African Countries (MultiChoice spec)
      blockoutCountries: [
        // Special group for francophone countries
        { name: 'Agency', note: 'All 24 francophone African countries' },
        // Eastern Africa
        { name: 'Burundi', note: '' },
        { name: 'Comoros', note: '' },
        { name: 'Djibouti', note: '' },
        { name: 'Eritrea', note: '' },
        { name: 'Ethiopia', note: '' },
        { name: 'Kenya', note: '' },
        { name: 'Madagascar', note: '' },
        { name: 'Malawi', note: '' },
        { name: 'Mauritius', note: '' },
        { name: 'Mozambique', note: '' },
        { name: 'Rwanda', note: '' },
        { name: 'Seychelles', note: '' },
        { name: 'Somalia', note: '' },
        { name: 'South Sudan', note: '' },
        { name: 'Tanzania', note: '' },
        { name: 'Uganda', note: '' },
        { name: 'Zambia', note: '' },
        { name: 'Zimbabwe', note: '' },
        // Central Africa
        { name: 'Angola', note: '' },
        { name: 'Cameroon', note: '' },
        { name: 'Central African Republic', note: '' },
        { name: 'Chad', note: '' },
        { name: 'Democratic Republic of the Congo', note: '' },
        { name: 'Equatorial Guinea', note: '' },
        { name: 'Gabon', note: '' },
        { name: 'Republic of the Congo', note: '' },
        { name: 'São Tomé and Príncipe', note: '' },
        // Southern Africa
        { name: 'Botswana', note: '' },
        { name: 'Eswatini', note: 'Formerly Swaziland' },
        { name: 'Lesotho', note: '' },
        { name: 'Namibia', note: '' },
        { name: 'South Africa', note: '' },
        { name: 'Swaziland', note: 'Now Eswatini' },
        // Western Africa
        { name: 'Benin', note: '' },
        { name: 'Burkina Faso', note: '' },
        { name: 'Cape Verde', note: '' },
        { name: 'Côte d\'Ivoire', note: 'Ivory Coast' },
        { name: 'Gambia', note: '' },
        { name: 'Ghana', note: '' },
        { name: 'Guinea', note: '' },
        { name: 'Guinea-Bissau', note: '' },
        { name: 'Liberia', note: '' },
        { name: 'Mali', note: '' },
        { name: 'Mauritania', note: '' },
        { name: 'Niger', note: '' },
        { name: 'Nigeria', note: '' },
        { name: 'Senegal', note: '' },
        { name: 'Sierra Leone', note: '' },
        { name: 'Togo', note: '' }
      ],
      filteredBlockoutCountries: []
    }
  },
  methods: {
    ...mapActions(['getUser']),

    /**
     * Capture a JSON snapshot of the operator-editable fields. Compared against
     * the current state by `isDirty` computed. Call after initial load and after
     * any successful save.
     */
    captureFormSnapshot() {
      this.initialSnapshot = JSON.stringify(this.editableFormFields)
    },

    /**
     * Browser-level unsaved-changes guard. Triggered on tab close / refresh /
     * external nav. Modern browsers ignore custom messages but still display a
     * generic "Leave site?" prompt as long as `returnValue` is set.
     */
    onBeforeUnload(e) {
      if (this.isDirty) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
    },

    backToManageEpisodes() {
      this.$router.push({
        name: 'ManageEpisodes',
        params: {
          id: this.added ? this.$route.params.id : this.item.mediaId
        }
      })
    },
    /**
     * Refresh ONLY status/workflow/link fields. Does NOT touch user-editable fields
     * (title, description, episodeNumber, ageRestriction, episodeLength, releaseDate,
     * VOD/EPG settings, etc.) — preserves any in-flight edits the operator has made.
     *
     * Use this after actions that update server-side workflow state but should not
     * blow away the form: confirmTxLink, rejectTxLink, approve-for-playout, and
     * after submit() when the saved values are already in this.item.
     */
    async refreshStatusFields() {
      let episode = await EpisodeApi.get(this.$route.params.id)
      if (!episode) return

      // File tracking / processing status
      this.item.sourceFilename = episode.source_filename || ''
      this.item.storagePath = episode.storage_path || ''
      this.item.storageConfigId = episode.storage_config_id || null
      this.item.stitchedFilePath = episode.stitched_file_path || ''
      this.item.txFilename = episode.tx_filename || ''
      this.item.txFileExtension = episode.tx_file_extension || ''
      this.item.fileStatus = episode.file_status || 'pending'
      this.item.fileOrigin = episode.file_origin || 'new'

      // Stitch + QC
      this.item.stitchStatus = episode.stitch_status || 'not_started'
      this.item.stitchQcStatus = episode.stitch_qc_status || null
      this.item.stitchQcResultId = episode.stitch_qc_result_id || null

      // TX workflow + version tracking
      this.item.txWorkflowStatus = episode.tx_workflow_status || 'not_linked'
      this.item.txFileEtag = episode.tx_file_etag || null
      this.item.txFileEtagAt = episode.tx_file_etag_at || null
      this.item.txFileSizeBytes = episode.tx_file_size_bytes || null
      this.item.needsSoftSegmentation = episode.needs_soft_segmentation === true || episode.needs_soft_segmentation === 1

      // Traffic Agent link status
      this.item.linkStatus = episode.link_status || 'not_linked'
      this.item.linkSource = episode.link_source || null
      this.item.confirmedBy = episode.confirmed_by || null
      this.item.confirmedAt = episode.confirmed_at || null
      this.item.txFilePath = episode.tx_file_path || ''

      // Playout delivery
      this.item.playoutDeliveryStatus = episode.playout_delivery_status || 'not_started'
      this.item.playoutApprovedAt = episode.playout_approved_at || null
      this.item.playoutDeliveredAt = episode.playout_delivered_at || null
      this.item.playoutDestinationPath = episode.playout_destination_path || ''
      this.item.playoutDeliveryError = episode.playout_delivery_error || ''

      // SMB staging
      this.item.smbStagingStatus = episode.smb_staging_status || 'not_staged'
      this.item.smbStagedAt = episode.smb_staged_at || null
      this.item.smbStagedPath = episode.smb_staged_path || ''
      this.item.smbStagingError = episode.smb_staging_error || ''

      // Refresh segment counts (segment linking changes linkedSegmentCount)
      try {
        const segments = await SegmentsApi.getAllByEpisodeId(episode.id)
        if (segments && segments.length > 0) {
          this.segmentCount = segments.length
          const totalSeconds = segments.reduce((sum, s) => sum + (s.duration_seconds || 0), 0)
          this.segmentTotalMinutes = Math.ceil(totalSeconds / 60)
          this.linkedSegmentCount = segments.filter(s => s.source_storage_path).length
        } else {
          this.segmentCount = 0
          this.segmentTotalMinutes = 0
          this.linkedSegmentCount = 0
        }
      } catch (segErr) {
        console.warn('Failed to refresh segment count:', segErr)
      }
    },
    async setData() {
      let episode = await EpisodeApi.get(this.$route.params.id)
      if (episode) {
        this.item.id = episode.id
        this.item.title = episode.title
        this.item.description = episode.description
        this.item.episodeNumber = episode.episode_number
        this.item.mediaId = episode.media_id

        // Show and season info for header display
        this.item.showTitle = episode.show_title || ''
        this.item.seasonNumber = episode.season_number || null

        // File tracking fields (Phase 2 - Dual filename system)
        this.item.sourceFilename = episode.source_filename || ''
        this.item.storagePath = episode.storage_path || ''
        this.item.storageConfigId = episode.storage_config_id || null
        this.item.stitchedFilePath = episode.stitched_file_path || ''
        this.item.txFilename = episode.tx_filename || ''
        this.item.txFileExtension = episode.tx_file_extension || ''
        this.item.vodFilename = episode.vod_filename || ''
        this.item.vodDeliveryEnabled = episode.vod_delivery_enabled || false

        // Playout delivery fields
        this.item.playoutDeliveryEnabled = episode.playout_delivery_enabled || false
        this.item.playoutDeliveryStatus = episode.playout_delivery_status || 'not_started'
        this.item.playoutApprovedAt = episode.playout_approved_at || null
        this.item.playoutDeliveredAt = episode.playout_delivered_at || null
        this.item.playoutDestinationPath = episode.playout_destination_path || ''
        this.item.playoutDeliveryError = episode.playout_delivery_error || ''
        this.item.fileStatus = episode.file_status || 'pending'
        this.item.fileOrigin = episode.file_origin || 'new'

        // Stitch and QC status fields
        this.item.stitchStatus = episode.stitch_status || 'not_started'
        this.item.stitchQcStatus = episode.stitch_qc_status || null
        this.item.stitchQcResultId = episode.stitch_qc_result_id || null

        // TX Workflow Status and Version Tracking (OUTPUT phase)
        this.item.txWorkflowStatus = episode.tx_workflow_status || 'not_linked'
        this.item.txFileEtag = episode.tx_file_etag || null
        this.item.txFileEtagAt = episode.tx_file_etag_at || null
        this.item.txFileSizeBytes = episode.tx_file_size_bytes || null
        // Default to false - assume properly segmented unless explicitly marked true
        this.item.needsSoftSegmentation = episode.needs_soft_segmentation === true || episode.needs_soft_segmentation === 1

        // Traffic Agent link status fields
        this.item.linkStatus = episode.link_status || 'not_linked'
        this.item.linkSource = episode.link_source || null
        this.item.confirmedBy = episode.confirmed_by || null
        this.item.confirmedAt = episode.confirmed_at || null
        this.item.txFilePath = episode.tx_file_path || ''
        this.item.smbStagingStatus = episode.smb_staging_status || 'not_staged'
        this.item.smbStagedAt = episode.smb_staged_at || null
        this.item.smbStagedPath = episode.smb_staged_path || ''
        this.item.smbStagingError = episode.smb_staging_error || ''

        if (episode.episodedata && episode.episodedata !== '') {
          episode.episodedata = JSON.parse(episode.episodedata)
          if (episode.episodedata.releaseDate) {
            this.item.releaseDate = new Date(episode.episodedata.releaseDate)
          }
          this.item.ageRestriction = episode.episodedata.ageRestriction
          this.item.episodeLength = episode.episodedata.episodeLength
          // VOD dates from episodedata (legacy fallback)
          if (episode.episodedata.vodStartDate && !episode.vod_start_datetime) {
            this.item.vodStartDate = new Date(episode.episodedata.vodStartDate)
          }
          if (episode.episodedata.vodExpiryDate && !episode.vod_expiry_datetime) {
            this.item.vodExpiryDate = new Date(episode.episodedata.vodExpiryDate)
          }

          // EPG Configuration - load blockouts from episodedata
          if (episode.episodedata.epgBlockouts && episode.episodedata.epgBlockouts.length > 0) {
            // Convert string array to objects for taginput component
            this.item.epgBlockouts = episode.episodedata.epgBlockouts.map(name => {
              return this.blockoutCountries.find(c => c.name === name) || { name: name, note: '' }
            })
          } else {
            this.item.epgBlockouts = []
          }
        }

        // VOD dates from auto-calculated columns (priority over episodedata)
        if (episode.vod_start_datetime) {
          this.item.vodStartDate = new Date(episode.vod_start_datetime)
        }
        if (episode.vod_expiry_datetime) {
          this.item.vodExpiryDate = new Date(episode.vod_expiry_datetime)
        }

        // Load segment count and linked segment count for status display
        try {
          const segments = await SegmentsApi.getAllByEpisodeId(episode.id)
          if (segments && segments.length > 0) {
            this.segmentCount = segments.length
            const totalSeconds = segments.reduce((sum, s) => sum + (s.duration_seconds || 0), 0)
            this.segmentTotalMinutes = Math.ceil(totalSeconds / 60)
            // Count how many segments have source files linked
            this.linkedSegmentCount = segments.filter(s => s.source_storage_path).length
          } else {
            this.segmentCount = 0
            this.segmentTotalMinutes = 0
            this.linkedSegmentCount = 0
          }
        } catch (segErr) {
          console.warn('Failed to load segment count:', segErr)
        }
      }
    },

    async updateTxFileExtension(ext) {
      if (ext === this.item.txFileExtension) return

      try {
        await EpisodeApi.update({
          episodeId: this.item.id,
          txFileExtension: ext
        })
        this.item.txFileExtension = ext
        this.$buefy.toast.open({
          message: `File extension updated to .${ext}`,
          position: 'is-top',
          type: 'is-success'
        })
      } catch (err) {
        console.error('Failed to update file extension:', err)
        this.$buefy.toast.open({
          message: 'Failed to update file extension',
          position: 'is-top',
          type: 'is-danger'
        })
      }
    },

    /**
     * Connect audio monitor to video element when playback starts
     * Using @play instead of @canplay to ensure video is actually playing
     */
    onVideoPlay() {
      if (this.$refs.mediaPlayer && this.$refs.mediaPlayer.$refs.videoPlayer) {
        const video = this.$refs.mediaPlayer.$refs.videoPlayer
        if (video && video.src && !video.error) {
          this.audioMonitorVideoElement = video
        }
      }
    },

    /**
     * Filter EPG blockout countries based on search text
     */
    filterBlockoutCountries(text) {
      this.filteredBlockoutCountries = this.blockoutCountries.filter(country => {
        return country.name.toLowerCase().indexOf(text.toLowerCase()) >= 0
      })
    },

    /**
     * Form submit wrapper that surfaces validation failures loudly.
     *
     * The previous `passes(submit)` pattern silently aborted submission when any
     * required field (Title, Episode No) was empty — the error rendered inline
     * at the field, which is at the top of a long form. Operators scrolled down
     * editing segments saw "click Update, nothing happens" and assumed the app
     * was broken.
     *
     * On validation failure we now: (1) toast the failed field names, (2) scroll
     * the first invalid field into view, (3) focus its input. Save only proceeds
     * when validation passes.
     */
    async onSubmitClick() {
      const isValid = await this.$refs.observer.validate()

      if (!isValid) {
        // Collect failing field names from the observer's error bag
        const errorBag = this.$refs.observer.errors || {}
        const failedFields = Object.keys(errorBag).filter(k => errorBag[k] && errorBag[k].length > 0)

        const fieldList = failedFields.length > 0 ? failedFields.join(', ') : 'highlighted fields'
        this.$buefy.toast.open({
          message: `Cannot save: ${fieldList} required. Scroll up to see the highlighted field${failedFields.length > 1 ? 's' : ''}.`,
          type: 'is-danger',
          position: 'is-top',
          duration: 6000
        })

        // Scroll first invalid field into view and focus its input
        this.$nextTick(() => {
          const errorEl = this.$el.querySelector('.field.is-danger, .field .is-danger')
          if (errorEl) {
            const fieldRoot = errorEl.classList.contains('field') ? errorEl : errorEl.closest('.field')
            ;(fieldRoot || errorEl).scrollIntoView({ behavior: 'smooth', block: 'center' })
            const input = (fieldRoot || errorEl).querySelector('input, textarea, select')
            if (input) {
              setTimeout(() => input.focus(), 400) // wait for smooth scroll
            }
          }
        })
        return
      }

      await this.submit()
    },

    async submit() {
      // Build episodedata object for saving
      const episodedata = {
        releaseDate: this.item.releaseDate,
        episodeLength: this.item.episodeLength,
        vodStartDate: this.item.vodStartDate,
        vodExpiryDate: this.item.vodExpiryDate,
        ageRestriction: this.item.ageRestriction
      }
      // Add EPG blockouts if set (convert from objects to string array)
      if (this.item.epgBlockouts && this.item.epgBlockouts.length > 0) {
        episodedata.epgBlockouts = this.item.epgBlockouts.map(c => c.name || c)
      }
      this.item.episodedata = episodedata

      // Also set the dedicated VOD datetime columns
      this.item.vod_start_datetime = this.item.vodStartDate || null
      this.item.vod_expiry_datetime = this.item.vodExpiryDate || null

      this.loading = true
      if (!this.item.id) {
        this.item.mediaId = this.$route.params.id
        EpisodeApi.create(this.item)
          .then(response => {
            this.$buefy.toast.open({
              message: 'Episode created successfully!',
              position: 'is-top',
              type: 'is-success'
            })
            // Reset dirty tracking BEFORE router.push so the leave guard doesn't prompt
            this.captureFormSnapshot()
            this.$router.push({
              name: 'ManageEpisodes',
              params: { id: this.$route.params.id }
            })
            this.loading = false
          })
          .catch(err => {
            console.log(err)
            this.$buefy.toast.open({
              message: 'Error saving episode',
              position: 'is-top',
              type: 'is-danger'
            })
            this.loading = false
          })
      } else {
        this.item.episodeId = this.item.id
        EpisodeApi.update(this.item)
          .then(async () => {
            this.$buefy.toast.open({
              message: 'Episode updated successfully!',
              position: 'is-top',
              type: 'is-success'
            })
            this.loading = false

            // Auto-regenerate scheduled segments (no Skip option — stale exports are worse than the
            // schedule shift). Backend scopes to future-only instances; past airings are untouched.
            try {
              const regenResult = await EpisodeApi.regenerateScheduleSegments(this.item.id)
              if (regenResult && regenResult.scheduledInstancesAffected > 0) {
                this.$buefy.toast.open({
                  message: `Updated ${regenResult.scheduledInstancesAffected} scheduled instance${regenResult.scheduledInstancesAffected > 1 ? 's' : ''} in day view`,
                  position: 'is-top',
                  type: 'is-success',
                  duration: 5000
                })
              }
            } catch (regenErr) {
              console.error('Failed to regenerate schedule segments:', regenErr)
              this.$buefy.toast.open({
                message: 'Episode saved but schedule regeneration failed — day view and exports may be stale. Please retry or contact support.',
                position: 'is-top',
                type: 'is-warning',
                duration: 7000
              })
            }

            // Refresh only status/workflow fields — DO NOT call setData() here.
            // setData() reloads title/description/episodeNumber and would clobber any
            // in-flight edits the user made after the form submitted.
            await this.refreshStatusFields()

            // Reset dirty tracking now that everything is persisted.
            this.captureFormSnapshot()
          })
          .catch(err => {
            console.log(err)
            this.$buefy.toast.open({
              message: 'Error saving episode',
              position: 'is-top',
              type: 'is-danger'
            })
            this.loading = false
          })
      }
    },
    showRecentChanges() {
      this.showRecentChangesModal = true
    },
    /**
     * Show QC result modal
     */
    showQcResult() {
      if (this.item.stitchQcResultId) {
        this.showViewQcResultModal = true
      }
    },
    async refreshData() {
      await this.setData()
    },

    /**
     * Handle upload complete event from UploadMediaAssets
     * Refreshes episode data to show updated file info and QC status
     */
    async onUploadComplete(uploadInfo) {
      console.log('Upload complete:', uploadInfo)

      // Refresh episode data to show updated storage path and file status
      await this.setData()

      // Show success message with QC job info if available
      if (uploadInfo.qcJobId) {
        this.$buefy.toast.open({
          message: `File uploaded and QC job #${uploadInfo.qcJobId} started`,
          position: 'is-top',
          type: 'is-success',
          duration: 5000
        })
      }
    },

    /**
     * Recalculate episode length from segments
     * This ensures the episodeLength is always accurate based on actual segment durations
     */
    async recalculateEpisodeLengthFromSegments() {
      if (!this.item.id) {
        // New episode, no segments yet
        return
      }

      try {
        const segments = await SegmentsApi.getAllByEpisodeId(this.item.id)

        if (!segments || segments.length === 0) {
          // No segments, keep existing episodeLength
          return
        }

        // Calculate total duration from all segments in minutes
        const totalSeconds = segments.reduce(
          (sum, segment) => sum + (segment.duration_seconds || 0),
          0
        )
        const totalMinutes = Math.ceil(totalSeconds / 60)

        // Update episodeLength with calculated value
        this.item.episodeLength = totalMinutes
        this.segmentCount = segments.length
        this.segmentTotalMinutes = totalMinutes

        console.log(
          `Episode length recalculated from ${segments.length} segments: ${totalMinutes} minutes (${totalSeconds} seconds)`
        )
      } catch (error) {
        console.error('Failed to recalculate episode length from segments:', error)
        // Don't fail the save, just keep existing episodeLength
      }
    },

    /**
     * Handle stitch job submission from EpisodeProcessingPanel
     * Emits event to notify ManageEpisodes of status change
     */
    onStitchJobSubmitted(jobInfo) {
      console.log('Stitch job submitted:', jobInfo)
      this.$buefy.notification.open({
        message: `Processing job submitted successfully!`,
        type: 'is-success',
        hasIcon: true,
        duration: 8000
      })

      // Notify ManageEpisodes via event bus
      episodeEventBus.$emit(EPISODE_EVENTS.JOB_SUBMITTED, {
        episodeId: this.item.id,
        mediaId: this.item.mediaId,
        jobType: jobInfo.jobType || 'stitch',
        status: 'queued'
      })
    },

    /**
     * Handle files linked from EpisodeProcessingPanel
     * Emits event to notify ManageEpisodes of file linking
     */
    onFilesLinked(linkData) {
      console.log('Files linked:', linkData)
      this.$buefy.toast.open({
        message: 'Source file linked successfully',
        type: 'is-success'
      })
      // Optionally refresh episode data
      this.refreshData()

      // Notify ManageEpisodes via event bus
      episodeEventBus.$emit(EPISODE_EVENTS.FILES_LINKED, {
        episodeId: this.item.id,
        mediaId: this.item.mediaId,
        ...linkData
      })
    },

    /**
     * Handle QC status change from EpisodeProcessingPanel
     * Emits event to notify ManageEpisodes of QC status change
     */
    onQcStatusChanged(qcStatus) {
      console.log('QC status changed:', qcStatus)
      // Update local item data if needed
      if (qcStatus?.qc?.status) {
        this.item.stitchQcStatus = qcStatus.qc.status
      }

      // Notify ManageEpisodes via event bus
      episodeEventBus.$emit(EPISODE_EVENTS.STATUS_CHANGED, {
        episodeId: this.item.id,
        mediaId: this.item.mediaId,
        stitchQcStatus: qcStatus?.qc?.status,
        stitchStatus: qcStatus?.stitch?.status
      })
    },

    /**
     * Handle external status change event from ManageEpisodes
     */
    handleExternalStatusChange(data) {
      // Only process if it's for this episode
      if (data.episodeId !== this.item.id) return

      console.log('External status change received:', data)

      // Update local status fields
      if (data.stitchStatus) this.item.stitchStatus = data.stitchStatus
      if (data.stitchQcStatus) this.item.stitchQcStatus = data.stitchQcStatus
    },

    /**
     * Handle external refresh request from ManageEpisodes
     */
    handleExternalRefreshRequest(data) {
      // Refresh if it's for this episode or this media
      if (data.episodeId === this.item.id || data.mediaId === this.item.mediaId) {
        console.log('External refresh request received, refreshing data...')
        this.refreshData()
      }
    },

    /**
     * Handle approve playout request from EpisodeProcessingPanel
     */
    onApprovePlayoutFromPanel({ episodeId }) {
      // Set the playout delivery enabled flag and save
      this.item.playoutDeliveryEnabled = true
      this.$buefy.dialog.confirm({
        title: 'Approve for Playout',
        message: 'This will mark the episode as approved and copy the TX file to the playout server. Continue?',
        confirmText: 'Approve',
        type: 'is-success',
        onConfirm: () => {
          this.submit()
        }
      })
    },

    /**
     * Get TX File status tag type
     * Uses consistent logic across ManageEpisodes and EditEpisode
     * Now includes OUTPUT phase tx_workflow_status
     */
    getTxFileStatusType() {
      // Primary question: Is there a TX file assigned?
      const hasTxFile = this.item.storagePath || this.item.stitchedFilePath
      const stitchStatus = this.item.stitchStatus
      const stitchQcStatus = this.item.stitchQcStatus
      const txWorkflowStatus = this.item.txWorkflowStatus

      // HIGHEST PRIORITY: Check tx_workflow_status for version changes
      if (txWorkflowStatus === 'version_changed') {
        return 'is-danger' // File changed since approval - CRITICAL
      }

      // 1. NO TX FILE - check if pipeline is in progress
      if (!hasTxFile) {
        if (stitchStatus === 'processing' || stitchStatus === 'queued' || stitchStatus === 'pending') {
          return 'is-warning' // Processing
        }
        if (stitchStatus === 'failed') {
          return 'is-danger' // Stitch Failed
        }
        return 'is-light' // No TX File
      }

      // 2. TX FILE EXISTS - check OUTPUT workflow status first
      if (txWorkflowStatus === 'staged') return 'is-success' // Staged to playout
      if (txWorkflowStatus === 'approved') return 'is-success' // Approved

      // 3. Check QC status
      if (stitchQcStatus === 'passed') return 'is-success' // TX Ready
      if (stitchQcStatus === 'passed_with_warnings') return 'is-success' // TX Ready*
      if (stitchQcStatus === 'overridden') return 'is-info' // QC Overridden
      if (stitchQcStatus === 'failed') return 'is-danger' // QC Failed
      if (stitchQcStatus === 'pending' || stitchQcStatus === 'processing') return 'is-warning' // QC Pending

      // TX file exists but no QC run (direct upload/assignment)
      return 'is-info' // Assigned (No QC)
    },

    /**
     * Get TX File status label
     * Uses consistent logic across ManageEpisodes and EditEpisode
     * Handles both single-file mode and segment-based mode
     * Now includes OUTPUT phase tx_workflow_status and version warnings
     */
    getTxFileStatusLabel() {
      // Primary question: Is there a TX file assigned?
      const hasTxFile = this.item.storagePath || this.item.stitchedFilePath
      const stitchStatus = this.item.stitchStatus
      const stitchQcStatus = this.item.stitchQcStatus
      const txWorkflowStatus = this.item.txWorkflowStatus
      const needsSoftSegmentation = this.item.needsSoftSegmentation

      // HIGHEST PRIORITY: Check for version change (file modified since approval)
      if (txWorkflowStatus === 'version_changed') {
        return 'Version Changed!'
      }

      // For segment-based episodes, check if all segments have source files linked
      // This is important because segment linking updates segments.source_storage_path
      // but NOT episodes.storage_path
      const isSegmentBased = this.segmentCount > 0
      const allSegmentsLinked = isSegmentBased && this.linkedSegmentCount >= this.segmentCount

      // 1. NO TX FILE - check segment mode and pipeline status
      if (!hasTxFile) {
        // For segment-based content, check segment linking status
        if (isSegmentBased) {
          if (allSegmentsLinked) {
            // All segments linked - ready for stitch
            if (stitchStatus === 'processing') return 'Stitching...'
            if (stitchStatus === 'queued') return 'Stitch Queued'
            return 'Assigned' // All segments linked, ready to stitch
          } else if (this.linkedSegmentCount > 0) {
            // Partial segments linked
            return `${this.linkedSegmentCount}/${this.segmentCount} Linked`
          }
        }

        // Check pipeline status
        if (stitchStatus === 'processing') return 'Processing...'
        if (stitchStatus === 'queued') return 'Queued'
        if (stitchStatus === 'pending') return 'Pending Stitch'
        if (stitchStatus === 'failed') return 'Stitch Failed'
        return 'No TX File'
      }

      // 2. TX FILE EXISTS - check OUTPUT workflow status
      if (txWorkflowStatus === 'staged') {
        return needsSoftSegmentation ? 'Staged*' : 'Staged'
      }
      if (txWorkflowStatus === 'approved') {
        return needsSoftSegmentation ? 'Approved*' : 'Approved'
      }

      // 3. Check QC status
      if (stitchQcStatus === 'passed') {
        return needsSoftSegmentation ? 'TX Ready*' : 'TX Ready'
      }
      if (stitchQcStatus === 'passed_with_warnings') return 'TX Ready*'
      if (stitchQcStatus === 'overridden') return 'QC Overridden'
      if (stitchQcStatus === 'failed') return 'QC Failed'
      if (stitchQcStatus === 'processing') return 'QC Running...'
      if (stitchQcStatus === 'pending') return 'QC Pending'

      // TX file exists but no QC run (direct upload/assignment)
      return needsSoftSegmentation ? 'TX Assigned*' : 'TX Assigned'
    },

    /**
     * Get Playout Delivery status tag type
     */
    getPlayoutDeliveryStatusType() {
      const status = this.item.playoutDeliveryStatus

      if (!this.item.playoutDeliveryEnabled) return 'is-light'

      switch (status) {
        case 'delivered':
        case 'verified':
          return 'is-success'
        case 'pending':
        case 'transferring':
          return 'is-warning'
        case 'failed':
          return 'is-danger'
        default:
          return 'is-light'
      }
    },

    /**
     * Get Playout Delivery status label
     */
    getPlayoutDeliveryStatusLabel() {
      if (!this.item.playoutDeliveryEnabled) {
        return 'Not Approved'
      }

      const status = this.item.playoutDeliveryStatus
      switch (status) {
        case 'pending':
          return 'Pending Delivery'
        case 'transferring':
          return 'Transferring...'
        case 'delivered':
          return 'Delivered'
        case 'verified':
          return 'Verified'
        case 'failed':
          return 'Delivery Failed'
        default:
          return 'Approved'
      }
    },

    /**
     * Get tooltip text for playout approval checkbox
     */
    getPlayoutApprovalTooltip() {
      if (!this.item.txFilename) {
        return 'TX filename not yet generated. Link source files and run processing to generate the TX filename.'
      }
      if (!this.canApprovePlayoutDelivery) {
        return 'QC must pass before approving for playout. The TX filename must be verified by QC to ensure the file is broadcast-ready.'
      }
      return `Approve to deliver this file to the playout server using the system-generated TX filename: ${this.item.txFilename}`
    },

    /**
     * Format date for display
     */
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    /**
     * Get Link Status tag type
     */
    getLinkStatusType() {
      switch (this.item.linkStatus) {
        case 'confirmed':
          return 'is-success'
        case 'soft_linked':
          return 'is-warning'
        case 'not_linked':
        default:
          return 'is-light'
      }
    },

    /**
     * Get Link Status label
     */
    getLinkStatusLabel() {
      switch (this.item.linkStatus) {
        case 'confirmed':
          return 'Confirmed'
        case 'soft_linked':
          return 'Pending Confirmation'
        case 'not_linked':
        default:
          return 'Not Linked'
      }
    },

    /**
     * Get Staging Status tag type
     */
    getStagingStatusType() {
      switch (this.item.smbStagingStatus) {
        case 'staged':
          return 'is-success'
        case 'staging':
        case 'pending':
          return 'is-warning'
        case 'failed':
          return 'is-danger'
        case 'cleaned':
          return 'is-info'
        case 'not_staged':
        default:
          return 'is-light'
      }
    },

    /**
     * Get Staging Status label
     */
    getStagingStatusLabel() {
      switch (this.item.smbStagingStatus) {
        case 'staged':
          return 'Staged to Playout'
        case 'staging':
          return 'Staging...'
        case 'pending':
          return 'Pending Staging'
        case 'failed':
          return 'Staging Failed'
        case 'cleaned':
          return 'Cleaned Up'
        case 'not_staged':
        default:
          return 'Not Staged'
      }
    },

    /**
     * Preview the linked TX file in media player
     */
    async previewTxFile() {
      if (!this.item.txFilePath) {
        this.$buefy.toast.open({
          message: 'No TX file path available',
          type: 'is-warning'
        })
        return
      }

      this.loadingTxPreview = true
      try {
        // Get presigned URL for the TX file via Traffic Agent API
        const result = await TrafficAgentApi.getTxFilePreviewUrl(this.episodeId)

        if (result && result.success && result.url) {
          this.txPreviewUrl = result.url
          // Open in a new window or modal
          window.open(result.url, '_blank')
        } else {
          throw new Error(result?.error || 'No URL returned')
        }
      } catch (err) {
        console.error('Failed to get TX file preview URL:', err)
        this.$buefy.toast.open({
          message: err?.data?.error || 'Failed to generate preview URL',
          type: 'is-danger'
        })
      } finally {
        this.loadingTxPreview = false
      }
    },

    /**
     * Extract bucket name from S3 path
     * Handles formats: s3://bucket/key or bucket/key
     */
    extractBucketFromPath(path) {
      if (!path) return ''
      // Remove s3:// prefix if present
      const cleanPath = path.replace(/^s3:\/\//, '')
      // Split by first slash
      const parts = cleanPath.split('/')
      return parts[0] || ''
    },

    /**
     * Extract key from S3 path
     * Handles formats: s3://bucket/key or bucket/key
     */
    extractKeyFromPath(path) {
      if (!path) return ''
      // Remove s3:// prefix if present
      const cleanPath = path.replace(/^s3:\/\//, '')
      // Split by first slash and rejoin everything after bucket
      const parts = cleanPath.split('/')
      return parts.slice(1).join('/') || ''
    },

    /**
     * Confirm the soft-linked TX file
     */
    async confirmTxLink() {
      // Validate episode ID before making API call
      const episodeId = parseInt(this.item.id)
      if (!episodeId || isNaN(episodeId)) {
        console.error('Invalid episode ID for confirm:', this.item.id, 'route params:', this.$route.params.id)
        this.$buefy.toast.open({
          message: 'Invalid episode ID. Please refresh the page and try again.',
          type: 'is-danger'
        })
        return
      }

      this.confirmingLink = true
      try {
        const result = await TrafficAgentApi.confirmLink(episodeId, {
          triggerStaging: false // For now, don't auto-stage
        })

        if (result.success) {
          this.$buefy.toast.open({
            message: 'TX file link confirmed',
            type: 'is-success'
          })
          // Refresh status fields only — must NOT clobber in-flight user edits
          // to title/description/episodeNumber (see Issue: title-clear on confirm).
          await this.refreshStatusFields()
        } else {
          throw new Error(result.error || 'Confirmation failed')
        }
      } catch (err) {
        console.error('Failed to confirm link:', err)
        this.$buefy.toast.open({
          message: err.response?.data?.error || err.message || 'Failed to confirm link',
          type: 'is-danger'
        })
      } finally {
        this.confirmingLink = false
      }
    },

    /**
     * Reject the soft-linked TX file
     */
    async rejectTxLink() {
      // Validate episode ID before showing dialog
      const episodeId = parseInt(this.item.id)
      if (!episodeId || isNaN(episodeId)) {
        console.error('Invalid episode ID for reject:', this.item.id, 'route params:', this.$route.params.id)
        this.$buefy.toast.open({
          message: 'Invalid episode ID. Please refresh the page and try again.',
          type: 'is-danger'
        })
        return
      }

      this.$buefy.dialog.prompt({
        message: 'Why are you rejecting this TX file link?',
        inputAttrs: {
          placeholder: 'Reason (optional)',
          maxlength: 500
        },
        trapFocus: true,
        onConfirm: async (reason) => {
          this.rejectingLink = true
          try {
            const result = await TrafficAgentApi.rejectLink(episodeId, reason)

            if (result.success) {
              this.$buefy.toast.open({
                message: 'TX file link rejected',
                type: 'is-warning'
              })
              // Refresh status fields only — see refreshStatusFields() doc.
              await this.refreshStatusFields()
            } else {
              throw new Error(result.error || 'Rejection failed')
            }
          } catch (err) {
            console.error('Failed to reject link:', err)
            this.$buefy.toast.open({
              message: err.response?.data?.error || err.message || 'Failed to reject link',
              type: 'is-danger'
            })
          } finally {
            this.rejectingLink = false
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.tx-status-display {
  padding: 8px 0;
}

.vod-delivery-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border-left: 3px solid #48c774;
}

.vod-delivery-toggle .b-checkbox {
  margin-bottom: 0;
}

/* Consolidated Workflow Status Bar */
.workflow-status-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.workflow-status-bar .status-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workflow-status-bar .status-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.5px;
}

/* Playout Approval Row */
.playout-approval-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background-color: #f0f7ff;
  border-radius: 4px;
  border-left: 3px solid #3273dc;
}

.playout-approval-row .b-checkbox {
  margin-bottom: 0;
}

.ml-2 {
  margin-left: 8px;
}

.link-status-display {
  display: flex;
  align-items: center;
  padding: 8px 0;
}

.staging-status-display {
  padding: 8px 0;
}

.link-actions-box {
  background-color: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  padding: 16px 20px;
  margin-top: 10px;
}

.link-actions-box .level {
  margin-bottom: 0;
}

/* Fix yellow warning tags to be more legible - dark text on yellow background */
.help .tag.is-warning {
  background-color: #f59e0b;
  color: #1a1a1a;
  font-weight: 500;
}

/* Fix yellow warning help text - make it more legible with darker orange */
.help.has-text-warning {
  color: #b45309 !important; /* Darker orange instead of yellow */
  font-weight: 500;
}

/* Make the TX status tag clickable when QC result exists */
.tx-status-display .tag.is-clickable {
  cursor: pointer;
}
.tx-status-display .tag.is-clickable:hover {
  opacity: 0.85;
}

/* Episode Preview with Audio Monitor - side by side layout */
.episode-preview-wrapper {
  display: flex;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  align-items: stretch;
}
.episode-preview-wrapper .video-container {
  flex: 1;
  min-width: 0;
}
.episode-preview-wrapper .audio-monitor-container {
  flex-shrink: 0;
  width: 70px;
  min-height: 280px;
}
.episode-preview-wrapper :deep(.media-player-wrapper) {
  height: 100%;
}
.episode-preview-wrapper :deep(video) {
  max-height: 500px;
}
</style>
